import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, MoreHorizontal, Pencil, Phone, Trash2, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/DropdownMenu';
import { DeleteStudentDialog } from './DeleteStudentDialog';
import { useStudents } from '../../contexts/StudentsContext';
import type { Student } from '../../types/student';
import { initialsOf, orDash, relativeTime, truncate } from '../../utils/format';
const HEADERS = ['Photo', 'Full names', 'Gender', 'Class', 'Father', 'Mother', 'Note', ''];
function PhotoThumb({
  student,
  size = 'md'
}: {
  student: Student;
  size?: 'md' | 'lg';
}) {
  return <Avatar className={`${size === 'lg' ? 'h-12 w-12' : 'h-10 w-10'} rounded-xl border border-[var(--glass-border)]`} data-id="element-347" data-mp="2rhuxxnegthcq">
      {student.photoURL ? <AvatarImage src={student.photoURL} alt={student.fullNames || 'Student photo'} className="object-cover" data-id="element-348" data-mp="32y3wock3udhz" /> : null}
      <AvatarFallback className="rounded-xl bg-muted text-[11px] font-semibold text-muted-foreground" data-id="element-349" data-mp="37q5ai2wx8ca4">
        {student.fullNames.trim() ? initialsOf(student.fullNames) : <User className="h-4 w-4" data-id="element-350" data-mp="2xz0xv9upx3ig" />}
      </AvatarFallback>
    </Avatar>;
}
function GenderBadge({
  student
}: {
  student: Student;
}) {
  if (!student.gender) return <span className="text-sm text-muted-foreground" data-id="element-351" data-mp="3ln20611aznpf">—</span>;
  const tone = student.gender === 'Male' ? 'bg-sky-500/12 text-sky-700 dark:text-sky-300' : student.gender === 'Female' ? 'bg-pink-500/12 text-pink-700 dark:text-pink-300' : 'bg-violet-500/12 text-violet-700 dark:text-violet-300';
  return <Badge className={`rounded-full border-0 ${tone}`} data-id="element-352" data-mp="20c1xao0wnbfa">{student.gender}</Badge>;
}
function RowActions({
  student
}: {
  student: Student;
}) {
  const {
    openDetail,
    openEditForm
  } = useStudents();
  const [deleting, setDeleting] = useState<Student | null>(null);
  return <>
      <DropdownMenu data-id="element-353" data-mp="btz7io9ktpav">
        <DropdownMenuTrigger asChild data-id="element-354" data-mp="6sjiym86x0nq">
          <button type="button" aria-label={`Actions for ${student.fullNames || 'this record'}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-white/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:hover:bg-white/10" data-id="element-355" data-mp="6tl79se7vjne">
            <MoreHorizontal className="h-4 w-4" data-id="element-356" data-mp="3k6wml4suptyq" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44" data-id="element-357" data-mp="3f83angt7qb0o">
          <DropdownMenuItem onClick={() => openDetail(student)} data-id="element-358" data-mp="3fapo1x2sbkqt">
            <Eye className="mr-2 h-4 w-4" data-id="element-359" data-mp="3abcteq5o99do" />
            View details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openEditForm(student)} data-id="element-360" data-mp="1dgzxajrxuicf">
            <Pencil className="mr-2 h-4 w-4" data-id="element-361" data-mp="18i5e3gq26rrs" />
            Edit record
          </DropdownMenuItem>
          <DropdownMenuSeparator data-id="element-362" data-mp="1g2pste7ns6a2" />
          <DropdownMenuItem variant="destructive" onClick={() => setDeleting(student)} data-id="element-363" data-mp="1g1emb6mtwkf9">
            <Trash2 className="mr-2 h-4 w-4" data-id="element-364" data-mp="1kzpw5hd4iuxh" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteStudentDialog student={deleting} onClose={() => setDeleting(null)} data-id="element-365" data-mp="1uqubwjamijtd" />
    </>;
}
function ParentCell({
  name,
  phone
}: {
  name: string;
  phone: string;
}) {
  return <div className="min-w-0" data-id="element-366" data-mp="x56hu51n4dl0">
      <p className="truncate text-sm text-foreground" data-id="element-367" data-mp="121wsudyl8izk">{orDash(name)}</p>
      {phone.trim() ? <p className="mt-0.5 flex items-center gap-1 truncate font-mono text-xs text-muted-foreground" data-id="element-368" data-mp="171j6goibw18w">
          <Phone className="h-3 w-3 shrink-0" data-id="element-369" data-mp="1c0vtpad43ac0" />
          {phone}
        </p> : <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-370" data-mp="2igoor0uy08dm">No telephone</p>}
    </div>;
}
export function StudentTable({
  students
}: {
  students: Student[];
}) {
  const {
    openDetail
  } = useStudents();
  return <>
      {/* Desktop table */}
      <div className="hidden overflow-x-auto scrollbar-slim md:block" data-id="element-371" data-mp="2l14k90k92e1z">
        <table className="w-full min-w-[980px] border-collapse text-left" data-id="element-372" data-mp="2h0aue1n8yp6g">
          <thead data-id="element-373" data-mp="2uzjza5605wv4">
            <tr className="border-b border-[var(--glass-hairline)]" data-id="element-374" data-mp="2v024hran7lf6">
              {HEADERS.map(header => <th key={header || 'actions'} scope="col" className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-375" data-mp="2pz63blhoeddg">
                  {header}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-376" data-mp="3doqped94lqqy">
            {students.map((student, index) => <motion.tr key={student.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            delay: Math.min(index * 0.02, 0.2)
          }} className="group border-b border-[var(--glass-hairline)] transition-colors last:border-0 hover:bg-white/50 dark:hover:bg-white/5" data-id="element-377" data-mp="38rgre97o86ri" data-mpi={index}>
                <td className="px-4 py-3" data-id="element-378" data-mp="a5knfhbzpq5l">
                  <PhotoThumb student={student} data-id="element-379" data-mp="a49obuthp6kt" />
                </td>
                <td className="max-w-[220px] px-4 py-3" data-id="element-380" data-mp="3sbp9to9kfkw6">
                  <button type="button" onClick={() => openDetail(student)} className="block max-w-full truncate text-left text-sm font-semibold text-foreground hover:text-brand focus-visible:outline-none focus-visible:underline" data-id="element-381" data-mp="15efsc2z3kec">
                    {orDash(student.fullNames)}
                  </button>
                  <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-382" data-mp="3eku1cd2zb5t4">Added {relativeTime(student.createdAtMs)}</p>
                </td>
                <td className="px-4 py-3" data-id="element-383" data-mp="2zol3nfyql0it">
                  <GenderBadge student={student} data-id="element-384" data-mp="2zpw5394plw6r" />
                </td>
                <td className="px-4 py-3" data-id="element-385" data-mp="34m4s18p5etoh">
                  <span className="text-sm text-foreground" data-id="element-386" data-mp="2li19zrb482qk">{orDash(student.className)}</span>
                </td>
                <td className="max-w-[180px] px-4 py-3" data-id="element-387" data-mp="2gqrmoxd1hbhu">
                  <ParentCell name={student.fatherName} phone={student.fatherPhone} data-id="element-388" data-mp="2gp8593fncueh" />
                </td>
                <td className="max-w-[180px] px-4 py-3" data-id="element-389" data-mp="2bq3tq6592kkz">
                  <ParentCell name={student.motherName} phone={student.motherPhone} data-id="element-390" data-mp="26u6fq6hglqbd" />
                </td>
                <td className="max-w-[220px] px-4 py-3" data-id="element-391" data-mp="34fknb74lw7is">
                  <p className="truncate text-sm text-muted-foreground" data-id="element-392" data-mp="2zi2ah5ngx85i">
                    {student.note.trim() ? truncate(student.note, 42) : '—'}
                  </p>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-393" data-mp="2pjmthvi5nf9q">
                  <RowActions student={student} data-id="element-394" data-mp="2pj3aebgrqku4" />
                </td>
              </motion.tr>)}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="space-y-3 p-4 md:hidden" data-id="element-395" data-mp="1e3enfk3illtb">
        {students.map(student => <li key={student.id} className="rounded-2xl border border-[var(--glass-hairline)] bg-white/55 p-4 dark:bg-white/5" data-id="element-396" data-mp="3psktr99npxmo">
            <div className="flex items-start gap-3" data-id="element-397" data-mp="3uojn7b9ue5ty">
              <PhotoThumb student={student} size="lg" data-id="element-398" data-mp="3jjuztd4x30l" />
              <div className="min-w-0 flex-1" data-id="element-399" data-mp="8bbexl1scahr">
                <button type="button" onClick={() => openDetail(student)} className="block max-w-full truncate text-left text-sm font-semibold text-foreground" data-id="element-400" data-mp="8c39qtnk646z">
                  {orDash(student.fullNames)}
                </button>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-id="element-401" data-mp="mcdzftd4tcqk">
                  <GenderBadge student={student} data-id="element-402" data-mp="rafi4d6nba1i" />
                  {student.className.trim() && <Badge variant="secondary" className="rounded-full" data-id="element-403" data-mp="2o3hf4x1vphhz">
                      {student.className}
                    </Badge>}
                </div>
              </div>
              <RowActions student={student} data-id="element-404" data-mp="ysn89enzffxr" />
            </div>

            <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--glass-hairline)] pt-3" data-id="element-405" data-mp="tulrx1hxxupz">
              <div className="min-w-0" data-id="element-406" data-mp="18j8ftrlmvust">
                <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-407" data-mp="18i6sannflfu7">Father</dt>
                <dd className="mt-0.5" data-id="element-408" data-mp="13l6wd5kaptgt">
                  <ParentCell name={student.fatherName} phone={student.fatherPhone} data-id="element-409" data-mp="fukn5ayk2358" />
                </dd>
              </div>
              <div className="min-w-0" data-id="element-410" data-mp="awj2illhdvrn">
                <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-411" data-mp="axu90t6b9hmg">Mother</dt>
                <dd className="mt-0.5" data-id="element-412" data-mp="pqnqgk5587tr">
                  <ParentCell name={student.motherName} phone={student.motherPhone} data-id="element-413" data-mp="ktnrsuami7co" />
                </dd>
              </div>
            </dl>

            {student.note.trim() && <p className="mt-3 rounded-xl bg-white/60 p-2.5 text-xs text-muted-foreground dark:bg-white/5" data-id="element-414" data-mp="1w9lwyn2ok98m">
                {truncate(student.note, 120)}
              </p>}
          </li>)}
      </ul>
    </>;
}